import React, { useContext, useRef } from 'react';
import InfoIcon from 'src/assets/icons/warning-circle.svg';
import DocumentUploadArea from '../DocumentUploadArea/DocumentUploadArea';
import DocumentsGrid from '../DocumentsGrid/DocumentsGrid';
import { useGetAdditionalDocuments } from 'src/shared/queries/useGetAdditionalDocuments';
import { AsyncDataHandler, Toast } from '@ds';
import { IToastr } from '@ds/elements/Toast/Toast.types';
import { BeneDocAdditionalContext } from 'src/shared/providers/DocumentUploadContextProvider';
import { IDocument } from 'src/shared/types/Documents';
import { useGetCaseDetails } from 'src/shared/queries/useGetCaseDetails';
import { NoDocumentsFound } from '../NoDocumentsFound/NoDocumentsFound';
import styles from '../DocumentUploadPage/DocumentUploadPage.module.scss';

const TITLES = {
  caseIdError: 'Case ID is null. Cannot upload document.',
  userIdError: 'User ID is undefined. Cannot upload document.',
  uploadError: 'Error uploading document:',
  title: 'Additional Documents',
  subtitle: 'What are Additional Documents?',
  description:
    'Sometimes we may need you to provide documents that aren’t asked for on your checklist. You can upload any additional documents that your immigration provider requests here.',
};

interface AdditionalDocumentsProps {
  additionalDocs: IDocument[];
  isLoadingAdditionalDocs: boolean;
  refetch: () => Promise<any>;
}

const AdditionalDocuments: React.FC<AdditionalDocumentsProps> = () => {
  const { caseId } = useContext(BeneDocAdditionalContext);
  const { data: additionalDocs, isLoading: isLoadingAdditionalDocs, refetch } = useGetAdditionalDocuments(caseId ?? 0);
  const { data: caseDetails } = useGetCaseDetails(caseId ?? 0);
  const isCaseActive = caseDetails?.isActive ?? false;
  const toastrRef = useRef<IToastr>(null);

  return (
    <div className={styles['case-docs-page']}>
      <p className={styles['title']}>{TITLES.title}</p>

      {isCaseActive && (
        <div className={styles['additional-description-container']}>
          <img src={InfoIcon} />
          <div className={styles['additional-header-body']}>
            <p className={styles['header']}>{TITLES.subtitle}</p>
            <p>{TITLES.description}</p>
          </div>
        </div>
      )}

      {isCaseActive && <DocumentUploadArea allowFromCobalt onUploadComplete={refetch} />}

      <AsyncDataHandler isLoading={isLoadingAdditionalDocs}>
        <div className={styles['documents-grid-container']}>
          <DocumentsGrid
            documents={additionalDocs || []}
            isReadonly
            showMenu={isCaseActive}
            emptyMessage={<NoDocumentsFound />}
          />
        </div>
      </AsyncDataHandler>
      <Toast ref={toastrRef} />
    </div>
  );
};

export default AdditionalDocuments;
